/**
 * Native feel — small, dependency-injected hooks used across the app.
 *
 *  - Haptic feedback for satisfying moments (habit complete, breathwork
 *    inhale/exhale, gratitude save).
 *  - Native share sheet for the weekly gratitude digest (falls back to a
 *    download on the web build via html2canvas' existing path).
 *  - Local notifications for daily reminders when running natively — the
 *    email reminder path stays intact for web users.
 *
 * All helpers are safe no-ops on the web build so the same JS bundle can
 * ship to both environments.
 */

import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Share } from '@capacitor/share';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';

export const isNative = () => {
  try {
    return Capacitor.isNativePlatform && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/* ---------------- Haptics ---------------- */

export const hapticLight = async () => {
  if (!isNative()) return;
  try { await Haptics.impact({ style: ImpactStyle.Light }); } catch { /* noop */ }
};

export const hapticMedium = async () => {
  if (!isNative()) return;
  try { await Haptics.impact({ style: ImpactStyle.Medium }); } catch { /* noop */ }
};

export const hapticSuccess = async () => {
  if (!isNative()) return;
  try { await Haptics.notification({ type: NotificationType.Success }); } catch { /* noop */ }
};


/* ---------------- Share sheet ---------------- */

export const nativeShare = async ({ title, text, url }) => {
  if (!isNative()) return false;
  try {
    await Share.share({ title, text, url, dialogTitle: title });
    return true;
  } catch (err) {
    // User cancelled or the platform can't share — signal caller to fall back.
     
    console.info('[native-feel] Share cancelled or unavailable:', err?.message || err);
    return false;
  }
};


/* ---------------- Local notifications (daily reminders) ----------------
 *
 * Requirements from PRD:
 *   - When installed as a native app, use device notifications by default.
 *   - When on the web, keep using backend Resend emails.
 *   - Never double-notify: on mobile we tell the backend to skip email by
 *     PUTing reminder_settings.channel = 'native'. On web the channel stays
 *     'email'. The backend scheduler already reads this channel field.
 *
 * The notification is scheduled locally (does not require APNs / FCM
 * registration for Phase 2) using an absolute date that repeats daily.
 */

const REMINDER_ID = 1001;  // stable id so re-scheduling replaces cleanly
const REMINDER_TITLES = [
  'Your ritual is waiting',
  'A moment for you',
  'Reconnect today',
  'Your Future Self is here',
];

/** Compute the next Date object for HH:MM local time. */
const _nextTriggerDate = (hhmm) => {
  const [hh, mm] = (hhmm || '08:00').split(':').map((s) => parseInt(s, 10) || 0);
  const now = new Date();
  const t = new Date();
  t.setHours(hh, mm, 0, 0);
  if (t <= now) t.setDate(t.getDate() + 1);
  return t;
};

export const requestNotificationPermission = async () => {
  if (!isNative()) return 'web';
  try {
    const res = await LocalNotifications.requestPermissions();
    return res?.display || 'unknown';
  } catch {
    return 'error';
  }
};

/** Schedule (or replace) the daily local reminder. `hhmm` is 'HH:MM'. */
export const scheduleDailyReminder = async (hhmm = '08:00') => {
  if (!isNative()) return { scheduled: false, reason: 'web' };
  try {
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      return { scheduled: false, reason: `permission_${permission}` };
    }

    // Cancel any prior reminder with the same id.
    try { await LocalNotifications.cancel({ notifications: [{ id: REMINDER_ID }] }); } catch { /* noop */ }

    const title = REMINDER_TITLES[Math.floor(Math.random() * REMINDER_TITLES.length)];
    await LocalNotifications.schedule({
      notifications: [
        {
          id: REMINDER_ID,
          title,
          body: 'Open Future Self and take today\u2019s step.',
          schedule: {
            at: _nextTriggerDate(hhmm),
            repeats: true,
            every: 'day',
            allowWhileIdle: true,
          },
        },
      ],
    });
    await Preferences.set({ key: 'fs_reminder_time', value: hhmm });
    return { scheduled: true, time: hhmm };
  } catch (err) {
     
    console.warn('[native-feel] scheduleDailyReminder failed:', err?.message || err);
    return { scheduled: false, reason: 'error' };
  }
};

/** Cancel the local reminder (user disabled reminders). */
export const cancelDailyReminder = async () => {
  if (!isNative()) return false;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: REMINDER_ID }] });
    await Preferences.remove({ key: 'fs_reminder_time' });
    return true;
  } catch {
    return false;
  }
};
